import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

interface CookieConsentBannerProps {
  /** Analytics toggle value to show initially (current choice, or off). */
  initialAnalytics: boolean;
  /** Persist the choice. */
  onSave: (choice: { analytics: boolean }) => void;
}

/**
 * Bottom-anchored cookie-consent banner. Gates non-essential analytics;
 * necessary state and error monitoring are always-on and shown for
 * transparency only. All copy is routed through i18n (`common:consent.*`).
 * Provider names (PostHog, Sentry, ...) are named only in the privacy policy
 * and Terms of Use, never in this UI copy.
 */
export function CookieConsentBanner({
  initialAnalytics,
  onSave,
}: CookieConsentBannerProps): React.ReactElement {
  const { t } = useTranslation(["common"]);
  const [customizing, setCustomizing] = useState(false);
  const [analytics, setAnalytics] = useState(initialAnalytics);

  return (
    <div
      role="region"
      aria-label={t("common:consent.title")}
      data-test="cookie-consent-banner"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-card p-4 text-card-foreground shadow-lg sm:p-6"
    >
      <div className="mx-auto flex max-w-4xl flex-col gap-4">
        <div>
          <h2 className="text-base font-semibold">
            {t("common:consent.title")}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("common:consent.description")}
          </p>
        </div>

        {customizing && (
          <div className="flex flex-col gap-3">
            <label className="flex items-start gap-3">
              <Checkbox checked disabled data-test="cookie-consent-necessary" />
              <span className="text-sm">
                <span className="font-medium">
                  {t("common:consent.categories.necessary.label")}
                </span>
                <span className="block text-muted-foreground">
                  {t("common:consent.categories.necessary.description")}
                </span>
              </span>
            </label>
            <label className="flex items-start gap-3">
              <Checkbox
                checked={analytics}
                onCheckedChange={(checked) => setAnalytics(checked === true)}
                data-test="cookie-consent-analytics"
              />
              <span className="text-sm">
                <span className="font-medium">
                  {t("common:consent.categories.analytics.label")}
                </span>
                <span className="block text-muted-foreground">
                  {t("common:consent.categories.analytics.description")}
                </span>
              </span>
            </label>
          </div>
        )}

        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          {customizing ? (
            <Button
              variant="default"
              onClick={() => onSave({ analytics })}
              data-test="cookie-consent-save"
            >
              {t("common:consent.save")}
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => setCustomizing(true)}
                data-test="cookie-consent-customize"
              >
                {t("common:consent.customize")}
              </Button>
              <Button
                variant="outline"
                onClick={() => onSave({ analytics: false })}
                data-test="cookie-consent-reject"
              >
                {t("common:consent.rejectNonEssential")}
              </Button>
              <Button
                variant="default"
                onClick={() => onSave({ analytics: true })}
                data-test="cookie-consent-accept-all"
              >
                {t("common:consent.acceptAll")}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
